import React from 'react';
import styled from 'styled-components';
import { Fade, Slide } from 'react-awesome-reveal';
import { PageContainer } from '@/components/layout';
import { Card, CardHeader, CardTitle, Badge } from '@/components/common';
import { useAuth } from '@/context';

// Mock student specific data
const mockStudentData = {
  enrolledCourses: 2,
  completedLessons: 24,
  upcomingClasses: 3,
  attendanceRate: 96,
  creditsRemaining: 8,
  nextPaymentDue: '2026-02-01',
  currentLevel: 'Intermediate',
};

const mockStudentSchedule = [
  {
    id: '1',
    date: '2026-01-09',
    time: '03:00 PM',
    endTime: '03:45 PM',
    subject: 'Piano',
    teacher: 'Sourav Maity',
    room: 'Piano Studio A',
    isToday: true,
  },
  {
    id: '2',
    date: '2026-01-11',
    time: '10:00 AM',
    endTime: '10:45 AM',
    subject: 'Piano',
    teacher: 'Rajesh Dayal',
    room: 'Piano Studio A',
    isToday: false,
  },
  {
    id: '3',
    date: '2026-01-14',
    time: '04:00 PM',
    endTime: '05:00 PM',
    subject: 'Music Theory',
    teacher: 'Mopa H Nymth',
    room: 'Group Lesson Room',
    isToday: false,
  },
];

const mockLearningProgress = {
  currentBook: 'Alfred Piano Book 3',
  currentChapter: 'Chapter 8: Scales and Arpeggios',
  overallProgress: 72,
  skills: [
    { name: 'Sight Reading', level: 70 },
    { name: 'Technique', level: 65 },
    { name: 'Theory', level: 80 },
    { name: 'Ear Training', level: 55 },
  ],
};

const mockAchievements = [
  { id: '1', title: 'First Recital', description: 'Performed at student recital', date: '2025-12-15', icon: '🎹' },
  { id: '2', title: 'Perfect Attendance', description: '3 months streak', date: '2025-11-30', icon: '⭐' },
  { id: '3', title: 'Theory Test Pass', description: 'Grade 3 with Merit', date: '2025-10-20', icon: '📜' },
];

const mockPracticeLog = [
  { day: 'Mon', minutes: 45 },
  { day: 'Tue', minutes: 30 },
  { day: 'Wed', minutes: 60 },
  { day: 'Thu', minutes: 0 },
  { day: 'Fri', minutes: 45 },
  { day: 'Sat', minutes: 90 },
  { day: 'Sun', minutes: 30 },
];

const WelcomeBanner = styled.div`
  background: ${({ theme }) => theme.colors.cardBgGradient};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  position: relative;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #8B5CF6 0%, #A78BFA 50%, #C4B5FD 100%);
  }
  
  &::after {
    content: '';
    position: absolute;
    right: -50px;
    top: -50px;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const WelcomeContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

const WelcomeTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamilyHeading};
  font-size: ${({ theme }) => theme.typography.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: ${({ theme }) => theme.colors.gold};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
`;

const WelcomeSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  margin: 0;
`;

const RoleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: 6px 12px;
  background: linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%);
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: #fff;
  margin-left: ${({ theme }) => theme.spacing.md};
`;

const CreditsDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  background: rgba(139, 92, 246, 0.15);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid rgba(139, 92, 246, 0.3);
`;

const CreditsLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CreditsValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: #8B5CF6;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled(Card)`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.lg};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #8B5CF6, #A78BFA);
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const StatIcon = styled.div<{ $color: string }>`
  width: 56px;
  height: 56px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: linear-gradient(135deg, ${({ $color }) => $color}20 0%, ${({ $color }) => $color}10 100%);
  color: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  font-family: ${({ theme }) => theme.typography.fontFamilyHeading};
  color: ${({ theme }) => theme.colors.text};
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${({ theme }) => theme.spacing.lg};
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const SideColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const UpcomingClassesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ClassItem = styled.div<{ $isToday?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ $isToday, theme }) => $isToday ? 'rgba(139, 92, 246, 0.15)' : theme.colors.backgroundTertiary};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: ${({ $isToday }) => $isToday ? '2px solid #8B5CF6' : 'none'};
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    transform: translateX(4px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const ClassDate = styled.div`
  min-width: 60px;
  text-align: center;
`;

const DateDay = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  color: ${({ theme }) => theme.colors.primary};
`;

const DateMonth = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ClassInfo = styled.div`
  flex: 1;
`;

const ClassSubject = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const ClassMeta = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ClassTime = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.gold};
`;

const TodayBadge = styled.span`
  padding: 4px 8px;
  background: linear-gradient(135deg, #8B5CF6, #A78BFA);
  color: white;
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  border-radius: ${({ theme }) => theme.borderRadius.full};
`;

const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;


const CurrentBook = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const CurrentChapter = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const OverallProgressBar = styled.div`
  height: 12px;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  overflow: hidden;
  margin: ${({ theme }) => theme.spacing.md} 0;
`;

const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background: linear-gradient(90deg, #8B5CF6, #A78BFA, #C4B5FD);
  border-radius: ${({ theme }) => theme.borderRadius.full};
  transition: width 0.5s ease;
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
`;

const SkillItem = styled.div`
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const SkillName = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const SkillBar = styled.div`
  height: 6px;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  overflow: hidden;
`;

const SkillFill = styled.div<{ $level: number }>`
  height: 100%;
  width: ${({ $level }) => $level}%;
  background: linear-gradient(90deg, #8B5CF6, #A78BFA);
  border-radius: ${({ theme }) => theme.borderRadius.full};
`;

const AchievementsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const AchievementItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const AchievementIcon = styled.div`
  font-size: 24px;
`;

const AchievementInfo = styled.div`
  flex: 1;
`;

const AchievementTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const AchievementDesc = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const PracticeChart = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  height: 100px;
  padding: ${({ theme }) => theme.spacing.md} 0;
`;

const PracticeBar = styled.div<{ $height: number }>`
  flex: 1;
  height: ${({ $height }) => Math.max($height, 5)}%;
  background: linear-gradient(180deg, #8B5CF6 0%, #A78BFA 100%);
  border-radius: ${({ theme }) => theme.borderRadius.sm} ${({ theme }) => theme.borderRadius.sm} 0 0;
  position: relative;
  
  &:hover::after {
    content: attr(data-minutes);
    position: absolute;
    top: -24px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 10px;
    color: ${({ theme }) => theme.colors.text};
    white-space: nowrap;
  }
`;

const PracticeLabels = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const PracticeLabel = styled.div`
  flex: 1;
  text-align: center;
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const BookClassButton = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.primaryGradient};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.colored};
  }
`;

const StudentDashboard: React.FC = () => {
  const { user } = useAuth();

  const stats = [
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      ),
      color: '#8B5CF6',
      label: 'Enrolled Courses',
      value: mockStudentData.enrolledCourses,
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="9 11 12 14 22 4" />
          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
        </svg>
      ),
      color: '#10B981',
      label: 'Completed Lessons',
      value: mockStudentData.completedLessons,
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      ),
      color: '#3B82F6',
      label: 'Upcoming Classes',
      value: mockStudentData.upcomingClasses,
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
      ),
      color: '#F59E0B',
      label: 'Attendance Rate',
      value: `${mockStudentData.attendanceRate}%`,
    },
  ];

  const maxPractice = Math.max(...mockPracticeLog.map(d => d.minutes));

  return (
    <PageContainer
      title="My Dashboard"
      subtitle="Track your musical journey"
    >
      <Fade triggerOnce>
        <WelcomeBanner>
          <WelcomeContent>
            <div>
              <WelcomeTitle>
                Welcome back, {user?.email?.split('@')[0]}!
                <RoleBadge>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 18V5l12-2v13" />
                    <circle cx="6" cy="18" r="3" />
                    <circle cx="18" cy="16" r="3" />
                  </svg>
                  Student
                </RoleBadge>
              </WelcomeTitle>
              <WelcomeSubtitle>
                Keep up the great work! You're on level: {mockStudentData.currentLevel}
              </WelcomeSubtitle>
            </div>
            <CreditsDisplay>
              <CreditsLabel>Credits Remaining</CreditsLabel>
              <CreditsValue>{mockStudentData.creditsRemaining}</CreditsValue>
            </CreditsDisplay>
          </WelcomeContent>
        </WelcomeBanner>
      </Fade>

      <Fade triggerOnce cascade damping={0.1}>
        <StatsGrid>
          {stats.map((stat, index) => (
            <StatCard key={index} padding="md">
              <StatIcon $color={stat.color}>{stat.icon}</StatIcon>
              <StatContent>
                <StatValue>{stat.value}</StatValue>
                <StatLabel>{stat.label}</StatLabel>
              </StatContent>
            </StatCard>
          ))}
        </StatsGrid>
      </Fade>

      <ContentGrid>
        <MainColumn>
          <Slide triggerOnce direction="up">
            <Card padding="lg">
              <CardHeader>
                <CardTitle>Upcoming Classes</CardTitle>
                <BookClassButton style={{ width: 'auto', padding: '8px 16px' }}>
                  Book a Class
                </BookClassButton>
              </CardHeader>
              <UpcomingClassesList>
                {mockStudentSchedule.map((cls) => {
                  const date = new Date(cls.date);
                  return (
                    <ClassItem key={cls.id} $isToday={cls.isToday}>
                      <ClassDate>
                        <DateDay>{date.getDate()}</DateDay>
                        <DateMonth>{date.toLocaleDateString('en-US', { month: 'short' })}</DateMonth>
                      </ClassDate>
                      <ClassInfo>
                        <ClassSubject>{cls.subject}</ClassSubject>
                        <ClassMeta>with {cls.teacher} • {cls.room}</ClassMeta>
                      </ClassInfo>
                      <ClassTime>{cls.time}</ClassTime>
                      {cls.isToday && <TodayBadge>Today</TodayBadge>}
                    </ClassItem>
                  );
                })}
              </UpcomingClassesList>
            </Card>
          </Slide>

          <Slide triggerOnce direction="up">
            <Card padding="lg">
              <CardHeader>
                <CardTitle>Learning Progress</CardTitle>
                <Badge variant="primary">{mockLearningProgress.overallProgress}% Complete</Badge>
              </CardHeader>
              <ProgressSection>
                <div>
                  <CurrentBook>{mockLearningProgress.currentBook}</CurrentBook>
                  <CurrentChapter>{mockLearningProgress.currentChapter}</CurrentChapter>
                </div>
                <OverallProgressBar>
                  <ProgressFill $progress={mockLearningProgress.overallProgress} />
                </OverallProgressBar>
                <SkillsGrid>
                  {mockLearningProgress.skills.map((skill, index) => (
                    <SkillItem key={index}>
                      <SkillName>{skill.name} ({skill.level}%)</SkillName>
                      <SkillBar>
                        <SkillFill $level={skill.level} />
                      </SkillBar>
                    </SkillItem>
                  ))}
                </SkillsGrid>
              </ProgressSection>
            </Card>
          </Slide>
        </MainColumn>

        <SideColumn>
          <Slide triggerOnce direction="right">
            <Card padding="lg">
              <CardHeader>
                <CardTitle>Weekly Practice</CardTitle>
                <Badge variant="success">{mockPracticeLog.reduce((a, b) => a + b.minutes, 0)} min</Badge>
              </CardHeader>
              <PracticeChart>
                {mockPracticeLog.map((day, index) => (
                  <PracticeBar
                    key={index}
                    $height={(day.minutes / maxPractice) * 100}
                    data-minutes={`${day.minutes}min`}
                  />
                ))}
              </PracticeChart>
              <PracticeLabels>
                {mockPracticeLog.map((day, index) => (
                  <PracticeLabel key={index}>{day.day}</PracticeLabel>
                ))}
              </PracticeLabels>
            </Card>
          </Slide>

          <Slide triggerOnce direction="right">
            <Card padding="lg">
              <CardHeader>
                <CardTitle>Achievements</CardTitle>
                <Badge variant="warning">{mockAchievements.length} earned</Badge>
              </CardHeader>
              <AchievementsList>
                {mockAchievements.map((achievement) => (
                  <AchievementItem key={achievement.id}>
                    <AchievementIcon>{achievement.icon}</AchievementIcon>
                    <AchievementInfo>
                      <AchievementTitle>{achievement.title}</AchievementTitle>
                      <AchievementDesc>{achievement.description}</AchievementDesc>
                    </AchievementInfo>
                  </AchievementItem>
                ))}
              </AchievementsList>
            </Card>
          </Slide>
        </SideColumn>
      </ContentGrid>
    </PageContainer>
  );
};

export default StudentDashboard;

